/** World-space geometry helpers (float64). */
export interface Vec2 {
  x: number;
  y: number;
}
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

/** Soft coordinate guard (§4.2). */
export const COORD_LIMIT = 1e9;
export const MIN_ZOOM = 1e-5;
export const MAX_ZOOM = 100;

export class CoordinateLimitError extends Error {
  readonly code = 'E_COORD_LIMIT';
  constructor() {
    super('Coordinates exceed the allowed range of ±1e9');
  }
}

export function withinLimits(r: Rect): boolean {
  return (
    Number.isFinite(r.x) &&
    Number.isFinite(r.y) &&
    Number.isFinite(r.w) &&
    Number.isFinite(r.h) &&
    Math.abs(r.x) <= COORD_LIMIT &&
    Math.abs(r.y) <= COORD_LIMIT &&
    Math.abs(r.x + r.w) <= COORD_LIMIT &&
    Math.abs(r.y + r.h) <= COORD_LIMIT
  );
}

/** Axis-aligned bounds of a rotated box (rotation around box center). */
export function boxBounds(b: Box): Rect {
  if (!b.rotation) return { x: b.x, y: b.y, w: b.w, h: b.h };
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const c = Math.abs(Math.cos(b.rotation));
  const s = Math.abs(Math.sin(b.rotation));
  const hw = (b.w * c + b.h * s) / 2;
  const hh = (b.w * s + b.h * c) / 2;
  return { x: cx - hw, y: cy - hh, w: hw * 2, h: hh * 2 };
}

export function boxCorners(b: Box): Vec2[] {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const c = Math.cos(b.rotation);
  const s = Math.sin(b.rotation);
  const pts: Vec2[] = [];
  for (const [lx, ly] of [
    [-b.w / 2, -b.h / 2],
    [b.w / 2, -b.h / 2],
    [b.w / 2, b.h / 2],
    [-b.w / 2, b.h / 2],
  ]) {
    pts.push({ x: cx + lx * c - ly * s, y: cy + lx * s + ly * c });
  }
  return pts;
}

/** World point → box-local point (0..w, 0..h). */
export function worldToLocal(b: Box, p: Vec2): Vec2 {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const dx = p.x - cx;
  const dy = p.y - cy;
  const c = Math.cos(-b.rotation);
  const s = Math.sin(-b.rotation);
  return { x: dx * c - dy * s + b.w / 2, y: dx * s + dy * c + b.h / 2 };
}

/** Box-local point → world point. */
export function localToWorld(b: Box, p: Vec2): Vec2 {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  const lx = p.x - b.w / 2;
  const ly = p.y - b.h / 2;
  const c = Math.cos(b.rotation);
  const s = Math.sin(b.rotation);
  return { x: cx + lx * c - ly * s, y: cy + lx * s + ly * c };
}

export function unionRects(rects: Iterable<Rect>): Rect | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.w > maxX) maxX = r.x + r.w;
    if (r.y + r.h > maxY) maxY = r.y + r.h;
  }
  if (minX === Infinity) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h;
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;
}

export function normalizeRect(x0: number, y0: number, x1: number, y1: number): Rect {
  return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

export function snap(v: number, step: number): number {
  return Math.round(v / step) * step;
}
